import {
  type CanonicalListFilterClause,
  parseListFilterDocumentParam,
} from "@/features/common/ui/filters/list-filter-document";

/**
 * Remembers, on this device, the last URL (path and query) of each list, so
 * a back link or a sidebar link opens the list with the same filters,
 * search, sort, and page. Saved views stay the durable, cross-device layer;
 * this only keeps the current working state.
 *
 * Every read and write tolerates a missing or failing `localStorage` and
 * falls back to the bare list path.
 */

/** Org-relative paths of the lists whose URL this module remembers. */
export const LIST_RETURN_PATHS = [
  "/ventas",
  "/pedidos",
  "/pedidos-vicus",
  "/preparacion",
  "/clientes",
  "/cobranzas",
  "/suscripciones",
  "/presupuestos",
  "/turnos",
  "/remitos",
  "/productos",
  "/precios",
  "/transferencias",
  "/inventario",
  "/movimientos-inventario",
  "/recuentos-stock",
  "/compras",
  "/ordenes",
  "/proveedores",
  "/pagos",
  "/tesoreria",
  "/tesoreria/liquidaciones",
  "/cheques",
  "/movimientos",
  "/conciliacion",
  "/reportes",
] as const;

export type ListReturnPath = (typeof LIST_RETURN_PATHS)[number];

/** Who the stored URLs belong to. Shared PCs keep one entry per member. */
export type ListReturnScope = {
  orgId: string;
  orgSlug: string;
  userId: string;
};

const STORAGE_KEY_PREFIX = "lapyme:list-return:v1";
const URL_BASE = "https://list-return.invalid";
const MAX_STORED_HREF_LENGTH = 8000;

/** Cursor params; a stored cursor can point at a page that no longer exists. */
const PAGINATION_PARAMS = ["after", "before", "page"] as const;
/**
 * Date params that lists keep outside the filter document, including the
 * shared date rule params of `list-filter-url-params.ts`.
 */
const DATE_PARAMS = [
  "dateFrom",
  "dateTo",
  "dateRule",
  "singleDate",
  "dateAmount",
  "dateUnit",
  "startDate",
  "endDate",
] as const;
const FILTER_PARAM = "filter";

const LIST_PATH_SET: ReadonlySet<string> = new Set(LIST_RETURN_PATHS);

export function listReturnStorageKey(
  scope: ListReturnScope,
  listPath: ListReturnPath
): string {
  return `${STORAGE_KEY_PREFIX}:${scope.userId}:${scope.orgId}:${listPath}`;
}

/** The bare list URL, as the app linked to it before this module. */
export function bareListHref(orgSlug: string, listPath: ListReturnPath) {
  return `/${orgSlug}${listPath}`;
}

/**
 * The list a pathname shows, or `null` when the pathname is not exactly a
 * known list of that organization (a detail page, for example).
 */
export function matchListReturnPath(
  pathname: string,
  orgSlug: string
): ListReturnPath | null {
  const prefix = `/${orgSlug}`;
  if (!pathname.startsWith(`${prefix}/`)) {
    return null;
  }
  const rest = pathname.slice(prefix.length).replace(/\/+$/u, "");
  return LIST_PATH_SET.has(rest) ? (rest as ListReturnPath) : null;
}

function parseRelativeHref(href: string): URL | null {
  if (!href.startsWith("/") || href.startsWith("//")) {
    return null;
  }
  try {
    const url = new URL(href, URL_BASE);
    return url.origin === URL_BASE ? url : null;
  } catch {
    return null;
  }
}

/**
 * Accepts only a same-origin URL whose path is exactly that list of that
 * organization, so a bad stored value cannot send the member elsewhere.
 * Returns the normalized path and query, without a hash.
 */
export function acceptListReturnHref(
  href: unknown,
  orgSlug: string,
  listPath: ListReturnPath
): string | null {
  if (typeof href !== "string" || href.length > MAX_STORED_HREF_LENGTH) {
    return null;
  }
  const url = parseRelativeHref(href);
  if (!url || url.pathname !== bareListHref(orgSlug, listPath)) {
    return null;
  }
  return `${url.pathname}${url.search}`;
}

function getStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

/** Subscribes to changes from this tab and from other tabs or windows. */
export function subscribeListReturn(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    // `key` is null when another tab clears the whole storage.
    if (event.key === null || event.key.startsWith(STORAGE_KEY_PREFIX)) {
      listener();
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

/** Saves the URL of a list. Ignores URLs of other paths and storage errors. */
export function saveListReturnHref(
  scope: ListReturnScope,
  listPath: ListReturnPath,
  href: string
): void {
  const accepted = acceptListReturnHref(href, scope.orgSlug, listPath);
  const storage = getStorage();
  if (!(accepted && storage)) {
    return;
  }
  const key = listReturnStorageKey(scope, listPath);
  try {
    if (storage.getItem(key) === accepted) {
      return;
    }
    storage.setItem(key, accepted);
  } catch {
    return;
  }
  notify();
}

/** The stored URL of a list, or the bare list path. */
export function readListReturnHref(
  scope: ListReturnScope | null,
  listPath: ListReturnPath
): string {
  const fallback = scope ? bareListHref(scope.orgSlug, listPath) : "";
  if (!scope) {
    return fallback;
  }
  const storage = getStorage();
  if (!storage) {
    return fallback;
  }
  try {
    return (
      acceptListReturnHref(
        storage.getItem(listReturnStorageKey(scope, listPath)),
        scope.orgSlug,
        listPath
      ) ?? fallback
    );
  } catch {
    return fallback;
  }
}

// Presets such as "Hoy" or "Este mes" are stored as fixed dates, so a
// sidebar link would reopen them on an old day. Relative deadlines
// ("próximos 7 días") keep their meaning and stay.
function isFixedDateClause(clause: CanonicalListFilterClause): boolean {
  switch (clause.type) {
    case "historicalDate":
    case "unrestrictedDate":
      return true;
    case "deadlineDate":
      return clause.value.kind !== "relative";
    default:
      return false;
  }
}

/**
 * The URL a sidebar link opens: the stored URL without the page cursor,
 * which may be stale, and without fixed dates, which would not follow the
 * calendar. Back links keep both, because they return to what the member
 * just saw.
 */
export function toSidebarListHref(href: string): string {
  const url = parseRelativeHref(href);
  if (!url) {
    return href;
  }
  const params = url.searchParams;
  for (const param of [...PAGINATION_PARAMS, ...DATE_PARAMS]) {
    params.delete(param);
  }
  const document = parseListFilterDocumentParam(params.get(FILTER_PARAM));
  if (document) {
    const clauses = document.clauses.filter(
      (clause) => !isFixedDateClause(clause)
    );
    // Keep an empty document rather than no param: on a saved view, a
    // missing filter would bring back the view's filters the member removed.
    if (clauses.length !== document.clauses.length) {
      params.set(FILTER_PARAM, JSON.stringify({ ...document, clauses }));
    }
  }
  const search = params.toString();
  return search ? `${url.pathname}?${search}` : url.pathname;
}

/** The stored URL for a sidebar link, or the bare list path. */
export function readSidebarListHref(
  scope: ListReturnScope | null,
  listPath: ListReturnPath
): string {
  return toSidebarListHref(readListReturnHref(scope, listPath));
}
